import { useState } from 'react';

import { ImageCard, type GalleryImage } from './image-card.js';

const MAX_IMAGE_SIZE_BYTES = 26_214_400;

interface ImageGalleryManagerProps<TImage extends GalleryImage> {
  readonly images: readonly TImage[];
  readonly imageBaseUrl: string;
  readonly categoryOptions?: readonly { readonly value: string; readonly label: string }[];
  readonly category?: string;
  readonly onCategoryChange?: (category: string) => void;
  readonly onUpload: (file: File) => Promise<void>;
  readonly onCover: (image: TImage) => Promise<void>;
  readonly onDelete: (image: TImage) => Promise<void>;
  readonly onUpdate: (image: TImage, altText: string) => Promise<void>;
  readonly onReorder: (ids: readonly string[]) => Promise<void>;
}

export function ImageGalleryManager<TImage extends GalleryImage>({
  images,
  imageBaseUrl,
  categoryOptions,
  category,
  onCategoryChange,
  onUpload,
  onCover,
  onDelete,
  onUpdate,
  onReorder,
}: ImageGalleryManagerProps<TImage>): React.JSX.Element {
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const upload = async (file: File): Promise<void> => {
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file.');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setError('Images must be 25 MB or smaller.');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      await onUpload(file);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'The image could not be uploaded.');
    } finally {
      setUploading(false);
    }
  };

  const reorder = async (sourceId: string, targetId: string): Promise<void> => {
    if (sourceId === targetId) return;
    const next = [...images];
    const sourceIndex = next.findIndex((image) => image.id === sourceId);
    const targetIndex = next.findIndex((image) => image.id === targetId);
    if (sourceIndex < 0 || targetIndex < 0) return;
    const [source] = next.splice(sourceIndex, 1);
    if (source === undefined) return;
    next.splice(targetIndex, 0, source);
    await onReorder(next.map((image) => image.id));
  };

  return (
    <section className="space-y-6 rounded-xl border border-border bg-card p-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            Gallery
          </p>
          <h2 className="text-lg font-semibold">Existing gallery</h2>
          <p className="text-sm text-muted-foreground">
            Review, reorder, cover and remove published images.
          </p>
        </div>
        {categoryOptions && onCategoryChange && category !== undefined ? (
          <label className="text-sm font-medium">
            Category
            <select
              className="ml-2 rounded border border-input bg-background px-3 py-2"
              value={category}
              onChange={(event) => {
                onCategoryChange(event.target.value);
              }}
            >
              {categoryOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      <label className="flex min-h-36 cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-border text-center hover:border-primary">
        <span className="text-sm font-medium">
          {uploading ? 'Uploading image…' : 'Drop images here or click to browse'}
        </span>
        <span className="mt-1 text-xs text-muted-foreground">JPG, PNG or WebP up to 25 MB</span>
        <input
          className="sr-only"
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={uploading}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file !== undefined) void upload(file);
            event.target.value = '';
          }}
        />
      </label>

      {error ? (
        <p className="rounded bg-destructive/10 p-3 text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
      {images.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No images have been uploaded for this selection.
        </p>
      ) : null}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {images.map((image) => {
          const imageUrl = image.url.startsWith('http') ? image.url : `${imageBaseUrl}${image.url}`;
          return (
            <ImageCard
              key={image.id}
              image={image}
              imageUrl={imageUrl}
              onCover={() => void onCover(image)}
              onDelete={() => void onDelete(image)}
              onUpdate={(altText) => void onUpdate(image, altText)}
              onDragStart={() => {
                setDraggedId(image.id);
              }}
              onDragOver={(event) => {
                event.preventDefault();
              }}
              onDrop={() => {
                if (draggedId !== null) void reorder(draggedId, image.id);
                setDraggedId(null);
              }}
            />
          );
        })}
      </div>
    </section>
  );
}
